import { useTranslation } from 'i18n';

/** Pick the variant for a set of chosen values (null = not chosen yet). */
export function findVariant(variants, chosen) {
  return (variants || []).find((v) => v.options.every((val, i) => chosen[i] == null || chosen[i] === val)) || null;
}

/** Default selection: ?variant=<id> when valid, else the first in-stock
 * variant (the default one first), else the default. */
export function initialVariant(variantData, urlVariantId) {
  const variants = variantData?.variants || [];
  if (!variants.length) return null;
  const fromUrl = urlVariantId ? variants.find((v) => String(v.id) === String(urlVariantId)) : null;
  if (fromUrl) return fromUrl;
  const def = variants.find((v) => v.is_default);
  const inStock = (v) => v.stock_level !== 'out';
  if (def && inStock(def)) return def;
  return variants.find(inStock) || def || variants[0];
}

/** Images for the selected variant: its colour's images first, then the
 * untagged ones; other colours' images are left out. */
export function variantGallery(variantData, variant) {
  const media = variantData?.media || [];
  if (!media.length) return [];
  const values = new Set(variant?.options || []);
  const tagged = media.filter((m) => m.option_value && values.has(m.option_value));
  const shared = media.filter((m) => !m.option_value);
  const list = tagged.length || shared.length ? [...tagged, ...shared] : media;
  return list.map((m) => ({ url: m.url, thumb: m.thumb_url || m.url, alt: m.alt || '' }));
}

/**
 * Colour swatches (hex / image) + size/text chips. A value that has no
 * enabled variant with the other chosen values is disabled; one whose only
 * matches are out of stock is struck through.
 */
export default function VariantSelector({ variantData, selected, onSelect, packageMode = false }) {
  const { t } = useTranslation();
  const options = variantData?.options || [];
  const variants = variantData?.variants || [];
  if (!options.length || !selected) return null;

  const pick = (optIdx, value) => {
    const chosen = selected.options.map((v, i) => (i === optIdx ? value : v));
    const exact = findVariant(variants, chosen);
    if (exact) return onSelect(exact);
    const loose = variants.filter((v) => v.options[optIdx] === value);
    onSelect(loose.find((v) => v.stock_level !== 'out') || loose[0] || selected);
  };

  return (
    <div className="space-y-4" data-testid="variant-selector">
      {options.map((opt, optIdx) => {
        const current = selected.options[optIdx];
        return (
          <fieldset key={opt.name}>
            <legend className="mb-2 text-sm font-semibold text-text">
              {packageMode ? t('variants.package') : opt.name}
              {current ? <span className="ms-2 font-normal text-muted">{current}</span> : null}
            </legend>
            <div className="flex flex-wrap gap-2">
              {opt.values.map((val) => {
                const others = selected.options.map((v, i) => (i === optIdx ? val.value : v));
                const match = findVariant(variants, others);
                const any = variants.filter((v) => v.options[optIdx] === val.value);
                const unavailable = !match && !any.length;
                const soldOut = (match ? match.stock_level === 'out' : any.every((v) => v.stock_level === 'out'));
                const active = current === val.value;
                const label = soldOut ? t('variants.soldOutValue', { value: val.value }) : val.value;
                if (opt.kind === 'color') {
                  return (
                    <button
                      key={val.value}
                      type="button"
                      onClick={() => pick(optIdx, val.value)}
                      disabled={unavailable}
                      aria-pressed={active}
                      aria-label={label}
                      title={label}
                      data-testid={`swatch-${val.value}`}
                      className={[
                        'relative h-10 w-10 rounded-full border-2 transition disabled:cursor-not-allowed disabled:opacity-30',
                        active ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-muted',
                      ].join(' ')}
                    >
                      <span
                        className="absolute inset-1 rounded-full border border-black/10 bg-cover bg-center"
                        style={val.image ? { backgroundImage: `url(${val.image})` } : { backgroundColor: val.hex || '#e2e8f0' }}
                      />
                      {soldOut && (
                        <span className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden>
                          <span className="h-[2px] w-full rotate-45 bg-danger" />
                        </span>
                      )}
                    </button>
                  );
                }
                return (
                  <button
                    key={val.value}
                    type="button"
                    onClick={() => pick(optIdx, val.value)}
                    disabled={unavailable}
                    aria-pressed={active}
                    aria-label={label}
                    data-testid={`chip-${val.value}`}
                    className={[
                      'min-w-[3rem] rounded-xl border px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-30',
                      active ? 'border-primary bg-primary/10 text-text' : 'border-border bg-surface text-text hover:border-muted',
                      soldOut ? 'text-muted line-through' : '',
                    ].join(' ')}
                  >
                    {val.value}
                  </button>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
